import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CursoFormDialog } from "@/components/forms/CursoFormDialog";
import { Plus, Trash2, ShieldAlert, Pencil, ChevronRight, UserCog } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  ativo: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  planejado: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  encerrado: "bg-slate-500/20 text-slate-300 border-slate-500/40",
};

export default function Instrucoes() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [instrucoes, setInstrucoes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/cursos?tipo=instrucao").then((r) => setInstrucoes(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (e, c) => { e.stopPropagation(); setEditing(c); setOpen(true); };
  const remove = async (e, id) => { e.stopPropagation(); await api.delete(`/cursos/${id}`); toast.success("Instrução removida"); load(); };
  const handleSaved = (created) => {
    load();
    if (created && created.id) navigate(`/instrucoes/${created.id}`);
  };

  return (
    <div>
      <PageHeader title="Instruções" subtitle="Instruções operacionais aplicáveis por instrutores nas suas unidades">
        <Button data-testid="nova-instrucao-btn" onClick={openNew}><Plus className="h-4 w-4 mr-2" />Nova instrução</Button>
      </PageHeader>

      <div className="grid sm:grid-cols-2 gap-4">
        {instrucoes.map((c) => (
          <Card key={c.id} data-testid={`instrucao-${c.id}`} onClick={() => navigate(`/instrucoes/${c.id}`)}
            className="p-5 bg-card border-border cursor-pointer hover:border-primary/60 hover:bg-card/70 transition-all">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/15"><ShieldAlert className="h-5 w-5 text-amber-400" /></div>
                <div>
                  <div className="font-semibold text-lg leading-tight">{c.nome}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={STATUS_BADGE[c.status] || "bg-secondary text-secondary-foreground border-border"}>{c.status}</Badge>
                    {c.permite_aplicacoes_instrutor && <Badge className="bg-primary/15 text-primary border-primary/30 gap-1"><UserCog className="h-3 w-3" />Instrutor pode aplicar</Badge>}
                  </div>
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2 shrink-0">
                  <button data-testid={`edit-instrucao-${c.id}`} onClick={(e) => openEdit(e, c)} className="text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`del-instrucao-${c.id}`} onClick={(e) => remove(e, c.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            {c.descricao && <p className="text-sm text-muted-foreground mt-3">{c.descricao}</p>}
            <div className="flex items-center gap-1 text-xs text-primary mt-3">Abrir instrução <ChevronRight className="h-3.5 w-3.5" /></div>
          </Card>
        ))}
        {!instrucoes.length && <p className="text-muted-foreground text-sm">Nenhuma instrução disponível.</p>}
      </div>

      <CursoFormDialog open={open} onOpenChange={setOpen} curso={editing} onSaved={handleSaved} defaultTipo="instrucao" />
    </div>
  );
}
